<div class="profile-options-container">
  <!-- CV Parsing Loading Overlay -->
  <div class="cv-parsing-loader" *ngIf="showCvParsingLoader">
    <div class="cv-parsing-loader-content">
      <div class="spinner">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">{{ 'common.loading' | transloco }}</span>
        </div>
      </div>
      <h4>{{ 'profile.aiParsing.validation.cvParsing.title' | transloco }}</h4>
      <p>{{ 'profile.aiParsing.validation.cvParsing.description' | transloco }}</p>
    </div>
  </div>

  <div class="profile-options-content" [class.loading]="showCvParsingLoader">
    <!-- Show different title/subtitle when user has existing data -->
    <ng-container *ngIf="!hasExistingProfileData">
      <h3 class="profile-options-title">{{ 'profile.cv.profileOptions.title' | transloco }}</h3>
      <p class="profile-options-subtitle">{{ 'profile.cv.profileOptions.subtitle' | transloco }}</p>
    </ng-container>
    <ng-container *ngIf="hasExistingProfileData">
      <h3 class="profile-options-title">{{ 'profile.cv.profileOptions.replaceTitle' | transloco }}</h3>
      <p class="profile-options-subtitle">{{ 'profile.cv.profileOptions.replaceSubtitle' | transloco }}</p>
    </ng-container>

    <div class="profile-options-cards">
      <div class="profile-option-card ai-card" [class.recommended]="!hasExistingProfileData" [class.collapsed]="hasExistingProfileData && !isAiSectionExpanded">
        <div class="recommended-badge" *ngIf="!hasExistingProfileData">{{ 'profile.cv.profileOptions.recommended' | transloco }}</div>

        <!-- Collapsible header - shown when user has existing data -->
        <div class="collapsible-header" *ngIf="hasExistingProfileData" (click)="toggleAiSection()">
          <div class="collapsible-header-content">
            <div class="profile-option-icon">
              <i class="fa-solid fa-star"></i>
            </div>
            <div class="collapsible-header-text">
              <h4>{{ 'profile.cv.profileOptions.aiTitle' | transloco }}</h4>
              <p>{{ 'profile.cv.profileOptions.replaceWithAiDescription' | transloco }}</p>
            </div>
          </div>
          <div class="collapsible-header-toggle">
            <i class="fas" [class.fa-chevron-down]="!isAiSectionExpanded" [class.fa-chevron-up]="isAiSectionExpanded"></i>
          </div>
        </div>

        <!-- Expanded content - always shown when no existing data, or when expanded -->
        <div class="profile-option-content" *ngIf="!hasExistingProfileData || isAiSectionExpanded">
          <div class="profile-option-icon" *ngIf="!hasExistingProfileData">
            <i class="fa-solid fa-star"></i>
          </div>
          <div class="profile-option-details">
            <ng-container *ngIf="!hasExistingProfileData">
              <h4>{{ 'profile.cv.profileOptions.aiTitle' | transloco }}</h4>
              <p>{{ 'profile.cv.profileOptions.aiDescription' | transloco }}</p>
            </ng-container>

            <form [formGroup]="resumeAndCoverForm" class="form">
              <div class="apply-form-content grid">
                <!-- Resume Upload -->
                <div class="form__group my-md">
                  <div class="file-dropzone" id="resume-dropzone" [class.has-file]="resumeAndCoverForm.get('resumeFile')?.value">
                    <div class="file-dropzone__upload-area" *ngIf="!resumeAndCoverForm.get('resumeFile')?.value">
                      <div class="file-dropzone__icon">
                        <i class="fas fa-file-upload"></i>
                      </div>
                      <div class="file-dropzone__text">
                        <p>{{ 'profile.resume.dragDrop' | transloco }}</p>
                        <span>{{ 'profile.resume.or' | transloco }}</span>
                      </div>
                      <app-button type="button" size="md" variant="outline" color="secondary" (click)="fileInput.click()">
                        {{ 'profile.resume.browse' | transloco }}
                      </app-button>
                      <p class="file-dropzone__info">{{ 'profile.resume.supportedFormats' | transloco }}</p>
                      <input type="file" hidden #fileInput (change)="onFileChange($event, 'resume')" accept=".pdf,.doc,.docx" />
                    </div>

                    <div class="file-dropzone__file-info" [class.hidden]="!resumeAndCoverForm.get('resumeFile')?.value">
                      <div class="file-dropzone__file-icon">
                        <i class="fas fa-file-pdf"></i>
                      </div>
                      <div class="file-dropzone__file-details">
                        <div class="file-dropzone__file-name">{{ resumeAndCoverForm.get('resumeFile')?.value?.name }}</div>
                        <div class="file-dropzone__file-size">{{ resumeAndCoverForm.get('resumeFile')?.value?.size | number }} KB</div>
                      </div>
                      <app-button type="button" variant="outline" color="secondary" (click)="removeFile('resume')">
                        <i class="fas fa-times"></i>
                      </app-button>
                    </div>
                  </div>
                </div>

                <app-select
                  formControlName="resumeLanguage"
                  [label]="'profile.personal.resumeLanguage' | transloco"
                  [options]="[
                    { value: 'sk', label: ('profile.personal.sk' | transloco) },
                    { value: 'cz', label: ('profile.personal.cz' | transloco) },
                    { value: 'en', label: ('profile.personal.en' | transloco) }
                  ]"
                  [placeholder]="'profile.personal.selectResumeLanguage' | transloco"
                  [required]="true"></app-select>

                <!-- AI Parsing Consent Checkbox -->
                <div class="form__group my-md">
                  <app-input-checkbox
                    formControlName="aiParsingConsent"
                    [label]="('jobApply.aiParsingConsent.label' | transloco) || 'I consent to AI processing of my CV'"
                    [required]="true"
                    [disabled]="aiParsingConsentUpdating"
                    [errorMessage]="
                      resumeAndCoverForm.get('aiParsingConsent')?.invalid && resumeAndCoverForm.get('aiParsingConsent')?.touched
                        ? ('jobApply.aiParsingConsent.error' | transloco) || 'You must consent to AI processing to use this feature'
                        : ''
                    "></app-input-checkbox>
                  <p class="form__group__description mt-sm" [innerHTML]="aiParsingConsentHtml"></p>
                </div>

                <!-- Overwrite Confirmation Checkbox - shown only when user has existing profile data -->
                <div class="form__group my-md" *ngIf="hasExistingProfileData">
                  <app-alert-card type="warning" [dismissible]="false" class="overwrite-warning-alert">
                    <i class="fas fa-exclamation-triangle"></i>
                    {{ 'profile.aiParsing.overwriteConfirmation.warning' | transloco }}
                  </app-alert-card>
                  <app-input-checkbox
                    formControlName="overwriteConfirmation"
                    [label]="('profile.aiParsing.overwriteConfirmation.label' | transloco) || 'I understand that my existing profile data will be replaced'"
                    [required]="true"
                    [errorMessage]="
                      resumeAndCoverForm.get('overwriteConfirmation')?.invalid && resumeAndCoverForm.get('overwriteConfirmation')?.touched && hasExistingProfileData
                        ? ('profile.aiParsing.overwriteConfirmation.error' | transloco) || 'You must confirm to proceed'
                        : ''
                    "></app-input-checkbox>
                  <p class="form__group__description mt-sm" *ngIf="overwriteConfirmationHtml" [innerHTML]="overwriteConfirmationHtml"></p>
                </div>
              </div>
            </form>
          </div>
        </div>

        <!-- Rate Limit Error Alert and Button - only shown when expanded -->
        <ng-container *ngIf="!hasExistingProfileData || isAiSectionExpanded">
          <app-alert-card *ngIf="rateLimitInfo" type="warning" [dismissible]="true" class="rate-limit-alert">
            {{ 'profile.aiParsing.rateLimit.message' | transloco }}
            <span *ngIf="rateLimitInfo.retryAfterHours || rateLimitInfo.retryAfterMinutes">
              {{ 'profile.aiParsing.rateLimit.retryIn' | transloco }}
              <strong *ngIf="rateLimitInfo.retryAfterHours">{{ rateLimitInfo.retryAfterHours }} {{ 'profile.aiParsing.rateLimit.hours' | transloco }}</strong>
              <strong *ngIf="rateLimitInfo.retryAfterMinutes">
                {{ rateLimitInfo.retryAfterMinutes }} {{ 'profile.aiParsing.rateLimit.minutes' | transloco }}
              </strong>
            </span>
          </app-alert-card>

          <app-button
            [block]="true"
            (click)="selectMethod(ProfileFillMethod.AI_AUTOFILL)"
            [disabled]="!isFormValidForAiParsing() || showCvParsingLoader || isParsingComplete || !!rateLimitInfo"
            type="button">
            <span *ngIf="!isParsingComplete">{{ 'profile.cv.profileOptions.useAi' | transloco }}</span>
            <span *ngIf="isParsingComplete">{{ 'profile.cv.profileOptions.aiComplete' | transloco }}</span>
          </app-button>
        </ng-container>
      </div>

      <!-- Manual entry option - hidden when user has existing data -->
      <div class="profile-option-card manual-card" *ngIf="!hasExistingProfileData">
        <div class="profile-option-content">
          <div class="profile-option-icon">
            <i class="fa-solid fa-pen"></i>
          </div>
          <div class="profile-option-details">
            <h4>{{ 'profile.cv.profileOptions.manualTitle' | transloco }}</h4>
            <p>{{ 'profile.cv.profileOptions.manualDescription' | transloco }}</p>
          </div>
        </div>
        <app-button
          [block]="true"
          (click)="selectMethod(ProfileFillMethod.MANUAL_ENTRY)"
          [disabled]="showCvParsingLoader"
          variant="outline"
          color="secondary"
          type="button">
          {{ 'profile.cv.profileOptions.useManual' | transloco }}
        </app-button>
      </div>
    </div>
  </div>
</div>
